/**
 * PreDeterminationStatusSection - Output section for pre-determination status
 *
 * Displays pre-determination status information as a top-level accordion section.
 */

import { AccordionContent, AccordionItem, AccordionTrigger } from '@components/ui/accordion';
import React from 'react';

import { FieldPathProvider } from '../context';
import { Field, SectionTriggerScore } from './Field';

interface PreDeterminationStatusSectionProps {
  data?: Record<string, unknown>;
  sectionId: string;
  entityType?: 'drug' | 'procedure';
}

export const PreDeterminationStatusSection: React.FC<PreDeterminationStatusSectionProps> = ({
  data,
  sectionId,
  entityType = 'drug',
}) => {
  const label = entityType === 'drug' ? 'Drug' : 'Procedure';

  return (
    <AccordionItem value={sectionId} className="border-b">
      <AccordionTrigger className="px-4 py-3 text-sm font-medium hover:bg-muted/50">
        <div className="flex items-center gap-2 flex-1">
          <span>{label} Pre Determination Status</span>
          <SectionTriggerScore subsectionKey="pre_determination_status" />
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-4">
        <FieldPathProvider prefix={`${entityType}.pre_determination_status`}>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            <Field
              label="Available"
              value={data?.pre_determination_available}
              fieldId="pre_determination_available"
            />
            <Field
              label="On File Exists"
              value={data?.pre_determination_on_file_exists}
              fieldId="pre_determination_on_file_exists"
            />
            <Field
              label="On File"
              value={data?.pre_determination_on_file}
              fieldId="pre_determination_on_file"
            />
            <Field
              label="Reference Number"
              value={data?.pre_determination_reference_number}
              fieldId="pre_determination_reference_number"
            />
            <Field
              label="Effective Date"
              value={data?.pre_determination_effective_date}
              fieldId="pre_determination_effective_date"
            />
            <Field
              label="End Date"
              value={data?.pre_determination_end_date}
              fieldId="pre_determination_end_date"
            />
            <Field
              label="Quantity Limits"
              value={data?.pre_determination_quantity_limits}
              fieldId="pre_determination_quantity_limits"
            />
            <Field
              label="Quantity Used"
              value={data?.pre_determination_quantity_limits_used}
              fieldId="pre_determination_quantity_limits_used"
            />
          </div>
        </FieldPathProvider>
      </AccordionContent>
    </AccordionItem>
  );
};
